import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI } from '../utils/api';
import { 
  Wrench, 
  Target, 
  ShieldAlert, 
  Users, 
  X,
  Calendar,
  User,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';

// Circular Metric Dial Component
const MetricDial = ({ 
  title, 
  icon: Icon, 
  color, 
  bgColor,
  metrics, 
  onMetricClick,
  testId 
}) => {
  // Use the first metric (Total) as the center value
  const centerValue = metrics[0]?.value || 0;
  
  // For the dial segments, only use the breakdown metrics (skip the first "Total" metric)
  const breakdownMetrics = metrics.slice(1);
  const breakdownTotal = breakdownMetrics.reduce((sum, m) => sum + m.value, 0);
  
  // Calculate percentages for the dial segments
  const segments = breakdownMetrics.reduce((acc, m, idx) => {
    const percent = breakdownTotal > 0 ? (m.value / breakdownTotal) * 100 : 0;
    const startPercent = idx === 0 ? 0 : acc[idx - 1].endPercent;
    const endPercent = startPercent + percent;
    acc.push({ ...m, percent, startPercent, endPercent });
    return acc;
  }, []);

  // Create SVG path for circular progress
  const radius = 45;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;

  return (
    <div 
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bgColor}`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {title}
          </h3>
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        {/* Circular Dial */}
        <div className="relative w-28 h-28">
          <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth={strokeWidth}
            />
            {/* Segment circles */}
            {segments.map((segment, idx) => {
              const offset = circumference - (segment.percent / 100) * circumference;
              const rotation = (segment.startPercent / 100) * 360;
              return (
                <circle
                  key={idx}
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="none"
                  stroke={segment.color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  style={{ 
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: '50% 50%'
                  }}
                />
              );
            })}
          </svg>
          {/* Center value */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-slate-900">{centerValue}</span>
          </div>
        </div>
        
        {/* Metrics Legend - Clickable items */}
        <div className="flex-1 space-y-2">
          {metrics.map((m, idx) => (
            <button
              key={idx}
              onClick={() => onMetricClick && onMetricClick(m.filterKey || m.label)}
              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
                <span className="text-sm text-slate-600">{m.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{m.value}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Drill-down Modal Component
const DrillDownModal = ({ title, icon: Icon, color, data, columns, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {title}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} className="text-left px-4 py-3 text-sm font-medium text-slate-700">
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {data.length > 0 ? (
                data.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-slate-50">
                    {columns.map((col, colIdx) => (
                      <td key={colIdx} className="px-4 py-3 text-sm text-slate-600">
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Quick Stat Card
const QuickStatCard = ({ label, value, icon: Icon, color, bgColor }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bgColor}`}>
      <Icon className={`w-6 h-6 ${color}`} />
    </div>
    <div>
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  </div>
);

const Dashboard = () => {
  const { user, canAccessMenu, isSuperAdmin } = useAuth();
  // V2-driven metric/quick-action visibility
  const canSeeJobs = canAccessMenu('jobs');
  const canSeeLeads = canAccessMenu('leads');
  const canSeeIncidents = canAccessMenu('incident-reporting') || canAccessMenu('incidents');
  const canSeeTeam = canAccessMenu('team-members') || canAccessMenu('team_members');
  const canSeeAttendance = canAccessMenu('time-attendance') || canAccessMenu('time_attendance');
  const canCreateJobs = isSuperAdmin();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillDownModal, setDrillDownModal] = useState(null);
  const [leaveConflicts, setLeaveConflicts] = useState([]);

  useEffect(() => {
    loadDashboardData();
    // Leave conflicts endpoint is admin-only; skip fetch for restricted users
    // to avoid a 403 in the console noise.
    if (isSuperAdmin()) {
      loadLeaveConflicts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeaveConflicts = async () => {
    try {
      const response = await analyticsAPI.getLeaveConflicts();
      setLeaveConflicts(response.data || []);
    } catch (error) {
      console.error('Failed to load leave conflicts:', error);
      setLeaveConflicts([]);
    }
  };

  const loadDashboardData = async () => {
    try {
      const response = await analyticsAPI.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
      // Fallback to basic stats
      try {
        const statsRes = await analyticsAPI.getStats();
        setDashboardData({
          jobs: {
            total: statsRes.data.total_jobs || 0,
            active: statsRes.data.in_progress_jobs || 0,
            completed: statsRes.data.completed_jobs || 0,
            detail: []
          },
          leads: { total: 0, assigned: 0, unassigned: 0, new: 0, started: 0, won: 0, lost: 0, detail: [] },
          incidents: { total: 0, by_severity: {}, open: 0, closed: 0 },
          team: { total_members: 0, assigned_today: 0, absent: 0, unassigned: 0, detail: [] }
        });
      } catch (e) {
        // Use empty data
        setDashboardData({
          jobs: { total: 0, active: 0, completed: 0, detail: [] },
          leads: { total: 0, assigned: 0, unassigned: 0, new: 0, started: 0, won: 0, lost: 0, detail: [] },
          incidents: { total: 0, by_severity: {}, open: 0, closed: 0 },
          team: { total_members: 0, assigned_today: 0, absent: 0, unassigned: 0, detail: [] }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const openJobsDrillDown = (filterKey) => {
    let filteredData = dashboardData?.jobs?.detail || [];
    let modalTitle = 'All Jobs';
    
    // Filter based on what was clicked
    if (filterKey === 'active') {
      filteredData = filteredData.filter(job => job.status === 'pending' || job.status === 'in_progress');
      modalTitle = 'Active Jobs';
    } else if (filterKey === 'completed') {
      filteredData = filteredData.filter(job => job.status === 'completed');
      modalTitle = 'Completed Jobs';
    }
    
    setDrillDownModal({
      title: modalTitle,
      icon: Wrench,
      color: 'bg-blue-600',
      data: filteredData,
      columns: [
        { header: 'Job ID', key: 'job_code', render: (row) => (
          <Link to={`/jobs/${row.id}`} className="text-blue-600 hover:underline font-mono">
            {row.job_code || row.id?.slice(0, 8)}
          </Link>
        )},
        { header: 'Description', key: 'title' },
        { header: 'Supervisor/Manager', key: 'assigned_manager_name', render: (row) => row.assigned_manager_name || '-' },
        { header: 'Creation Date', key: 'created_at', render: (row) => 
          row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy') : '-'
        },
        { header: 'Status', key: 'status', render: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'completed' ? 'bg-green-100 text-green-700' :
            row.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {row.status?.replace('_', ' ')}
          </span>
        )}
      ]
    });
  };

  const openLeadsDrillDown = (filterKey) => {
    let filteredData = dashboardData?.leads?.detail || [];
    let modalTitle = 'All Leads';
    
    // Helper function to check if lead is assigned
    const isAssigned = (lead) => {
      const assignedTo = lead.assigned_to;
      return assignedTo && assignedTo !== '' && assignedTo !== 'None' && assignedTo !== 'null';
    };
    
    // Filter based on what was clicked
    if (filterKey === 'assigned') {
      filteredData = filteredData.filter(lead => isAssigned(lead));
      modalTitle = 'Assigned Leads';
    } else if (filterKey === 'unassigned') {
      filteredData = filteredData.filter(lead => !isAssigned(lead));
      modalTitle = 'Unassigned Leads';
    }
    
    setDrillDownModal({
      title: modalTitle,
      icon: Target,
      color: 'bg-purple-600',
      data: filteredData,
      columns: [
        { header: 'Lead ID', key: 'lead_code', render: (row) => (
          <Link to={`/leads/${row.id}`} className="text-purple-600 hover:underline font-mono">
            {row.lead_code || row.id?.slice(0, 8)}
          </Link>
        )},
        { header: 'Company/Contact', key: 'company_name', render: (row) => row.company_name || row.contact_name || '-' },
        { header: 'Assigned To', key: 'assigned_to_name', render: (row) => row.assigned_to_name || 'Unassigned' },
        { header: 'Creation Date', key: 'created_at', render: (row) => 
          row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy') : '-'
        },
        { header: 'Status', key: 'status', render: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'won' || row.status === 'closed_won' ? 'bg-green-100 text-green-700' :
            row.status === 'lost' || row.status === 'closed_lost' ? 'bg-red-100 text-red-700' :
            row.status === 'started' ? 'bg-blue-100 text-blue-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {row.status?.replace('_', ' ')}
          </span>
        )}
      ]
    });
  };

  const openIncidentsDrillDown = () => {
    setDrillDownModal({
      title: 'Incidents Detail',
      icon: ShieldAlert,
      color: 'bg-red-600',
      data: dashboardData?.incidents?.detail || [],
      columns: [
        { header: 'Incident', key: 'title', render: (row) => (
          <Link to={`/incident-reporting`} className="text-red-600 hover:underline">
            {row.title || 'Untitled'}
          </Link>
        )},
        { header: 'Description', key: 'description', render: (row) => (
          <span className="truncate max-w-[200px] block">{row.description?.slice(0, 50) || '-'}{row.description?.length > 50 ? '...' : ''}</span>
        )},
        { header: 'Severity', key: 'severity', render: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.severity === 'critical' ? 'bg-red-100 text-red-700' :
            row.severity === 'high' ? 'bg-orange-100 text-orange-700' :
            row.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-green-100 text-green-700'
          }`}>
            {row.severity}
          </span>
        )},
        { header: 'Reported By', key: 'reported_by_name', render: (row) => row.reported_by_name || '-' },
        { header: 'Status', key: 'status', render: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'resolved' || row.status === 'closed' ? 'bg-green-100 text-green-700' :
            row.status === 'investigating' ? 'bg-blue-100 text-blue-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {row.status}
          </span>
        )},
        { header: 'Date', key: 'created_at', render: (row) => 
          row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy') : '-'
        }
      ]
    });
  };

  const openTeamDrillDown = (filterKey) => {
    let filteredData = [];
    let modalTitle = 'Team Members';
    
    const team = dashboardData?.team || {};
    
    if (filterKey === 'assigned') {
      filteredData = team.assigned_detail || [];
      modalTitle = 'Assigned Team Members';
    } else if (filterKey === 'absent') {
      filteredData = team.absent_detail || [];
      modalTitle = 'Absent Team Members (On Leave)';
    } else if (filterKey === 'unassigned') {
      filteredData = team.unassigned_detail || [];
      modalTitle = 'Available Team Members';
    } else if (filterKey === 'conflicts') {
      filteredData = team.conflict_detail || [];
      modalTitle = 'Conflicts - On Leave but Assigned to Jobs';
    } else {
      filteredData = team.detail || [];
      modalTitle = 'All Team Members';
    }
    
    setDrillDownModal({
      title: modalTitle,
      icon: Users,
      color: 'bg-emerald-600',
      data: filteredData,
      columns: [
        { header: 'Employee ID', key: 'employee_id', render: (row) => row.employee_id || '-' },
        { header: 'Name', key: 'name' },
        { header: 'Role', key: 'job_description', render: (row) => row.job_description || row.role || '-' },
        { header: 'Status', key: 'status', render: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'conflict' ? 'bg-red-100 text-red-700' :
            row.status === 'absent' ? 'bg-amber-100 text-amber-700' :
            row.status === 'assigned' ? 'bg-green-100 text-green-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {row.status_label || row.status}
          </span>
        )}
      ]
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const { jobs, leads, incidents, team } = dashboardData || {};

  return (
    <div data-testid="dashboard-page" className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Welcome back, {user?.name}!
        </h1>
        <p className="text-slate-600 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {format(new Date(), 'EEEE, dd MMMM yyyy')}
        </p>
      </div>

      {/* Metric Dials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Jobs Metrics Dial */}
        {canSeeJobs && (
        <MetricDial
          testId="jobs-metric-dial"
          title="Jobs"
          icon={Wrench}
          color="text-blue-600"
          bgColor="bg-blue-100"
          metrics={[
            { label: 'Total Jobs', value: jobs?.total || 0, color: '#3b82f6', filterKey: 'total' },
            { label: 'Active Jobs', value: jobs?.active || 0, color: '#f59e0b', filterKey: 'active' },
            { label: 'Completed', value: jobs?.completed || 0, color: '#22c55e', filterKey: 'completed' }
          ]}
          onMetricClick={openJobsDrillDown}
        />
        )}

        {/* Leads Metrics Dial */}
        {canSeeLeads && (
        <MetricDial
          testId="leads-metric-dial"
          title="Leads"
          icon={Target}
          color="text-purple-600"
          bgColor="bg-purple-100"
          metrics={[
            { label: 'Total Leads', value: leads?.total || 0, color: '#8b5cf6', filterKey: 'total' },
            { label: 'Assigned', value: leads?.assigned || 0, color: '#3b82f6', filterKey: 'assigned' },
            { label: 'Unassigned', value: leads?.unassigned || 0, color: '#94a3b8', filterKey: 'unassigned' }
          ]}
          onMetricClick={openLeadsDrillDown}
        />
        )}

        {/* Incidents Metrics Card */}
        {canSeeIncidents && (
        <div 
          data-testid="incidents-metric-card"
          className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer"
          onClick={openIncidentsDrillDown}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-100">
                <ShieldAlert className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Incidents
              </h3>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-slate-600">Open</span>
              </div>
              <span className="text-2xl font-bold text-slate-900">{incidents?.open || 0}</span>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm text-slate-600">Closed</span>
              </div>
              <span className="text-2xl font-bold text-slate-900">{incidents?.closed || 0}</span>
            </div>
          </div>
          
          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm text-slate-600 mb-2">By Severity:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(incidents?.by_severity || {}).map(([severity, count]) => (
                <span 
                  key={severity}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    severity === 'critical' ? 'bg-red-100 text-red-700' :
                    severity === 'high' ? 'bg-orange-100 text-orange-700' :
                    severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}
                >
                  {severity}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Team Metrics Card */}
        {canSeeTeam && (
        <div 
          data-testid="team-metric-card"
          className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Team Today
              </h3>
            </div>
            <button 
              onClick={() => openTeamDrillDown('all')}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          {/* Conflicts Alert */}
          {team?.conflicts > 0 && (
            <div 
              className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => openTeamDrillDown('conflicts')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    {team.conflicts} Conflict{team.conflicts > 1 ? 's' : ''} - Members on leave but assigned to jobs
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-red-400" />
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-3 gap-3">
            <div 
              className="bg-emerald-50 rounded-xl p-3 text-center cursor-pointer hover:bg-emerald-100 transition-colors"
              onClick={() => openTeamDrillDown('assigned')}
            >
              <p className="text-xs text-emerald-700 mb-1">Assigned</p>
              <span className="text-xl font-bold text-emerald-900">{team?.assigned_today || 0}</span>
            </div>
            <div 
              className="bg-amber-50 rounded-xl p-3 text-center cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => openTeamDrillDown('absent')}
            >
              <p className="text-xs text-amber-700 mb-1">Absent</p>
              <span className="text-xl font-bold text-amber-900">{team?.absent || 0}</span>
            </div>
            <div 
              className="bg-slate-50 rounded-xl p-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => openTeamDrillDown('unassigned')}
            >
              <p className="text-xs text-slate-700 mb-1">Unassigned</p>
              <span className="text-xl font-bold text-slate-900">{team?.unassigned || 0}</span>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-sm text-slate-600">Total Team Members</span>
            <span className="text-lg font-bold text-slate-900">{team?.total_members || 0}</span>
          </div>
        </div>
        )}
      </div>

      {/* Leave Conflicts Alert Section */}
      {leaveConflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 shadow-sm mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-100">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Leave Conflicts ({leaveConflicts.length})
              </h3>
              <p className="text-sm text-amber-700">Team members on leave assigned to active jobs</p>
            </div>
          </div>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {leaveConflicts.map((conflict, idx) => (
              <div 
                key={conflict.id || idx}
                className="bg-white rounded-xl p-4 border border-amber-100 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{conflict.member_name}</p>
                    <p className="text-sm text-slate-600">
                      {conflict.leave_type} • {conflict.leave_start ? format(new Date(conflict.leave_start), 'dd MMM') : ''} - {conflict.leave_end ? format(new Date(conflict.leave_end), 'dd MMM') : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Link 
                    to={`/jobs/${conflict.job_id}`}
                    className="text-sm font-medium text-amber-700 hover:text-amber-900"
                  >
                    {conflict.job_code || 'View Job'}
                  </Link>
                  <p className="text-xs text-slate-500">{conflict.job_title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats Row — leads-only, hidden if user has no leads access */}
      {canSeeLeads && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <QuickStatCard 
          label="New Leads" 
          value={leads?.new || 0} 
          icon={TrendingUp}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <QuickStatCard 
          label="Won Leads" 
          value={leads?.won || 0} 
          icon={CheckCircle2}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <QuickStatCard 
          label="Lost Leads" 
          value={leads?.lost || 0} 
          icon={XCircle}
          color="text-red-600"
          bgColor="bg-red-100"
        />
        <QuickStatCard 
          label="Started Leads" 
          value={leads?.started || 0} 
          icon={Clock}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
      </div>
      )}

      {/* Quick Actions — each action hidden unless the user has V2 access */}
      {(canCreateJobs || canSeeLeads || canSeeAttendance || canSeeIncidents) && (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="quick-actions">
        <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {canCreateJobs && (
          <Link 
            to="/jobs/new"
            data-testid="quick-action-new-job"
            className="flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
          >
            <Wrench className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">New Job</span>
          </Link>
          )}
          {canSeeLeads && (
          <Link 
            to="/leads"
            data-testid="quick-action-view-leads"
            className="flex items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors"
          >
            <Target className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">View Leads</span>
          </Link>
          )}
          {canSeeAttendance && (
          <Link 
            to="/time-attendance"
            data-testid="quick-action-time-attendance"
            className="flex items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
          >
            <Clock className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-900">Time & Attendance</span>
          </Link>
          )}
          {canSeeIncidents && (
          <Link 
            to="/incident-reporting"
            data-testid="quick-action-report-incident"
            className="flex items-center gap-3 p-4 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
          >
            <ShieldAlert className="w-5 h-5 text-red-600" />
            <span className="text-sm font-medium text-red-900">Report Incident</span>
          </Link>
          )}
        </div>
      </div>
      )}

      {/* Drill-down Modal */}
      {drillDownModal && (
        <DrillDownModal
          title={drillDownModal.title}
          icon={drillDownModal.icon}
          color={drillDownModal.color}
          data={drillDownModal.data}
          columns={drillDownModal.columns}
          onClose={() => setDrillDownModal(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
